import { useState } from "react";
import { Timer, Droplets, Coffee, Pause, Play, Settings2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useWellness } from "@/context/WellnessContext";

export default function WellnessPanel() {
  const {
    wellnessEnabled,
    minutesUntilBreak,
    isOnBreak,
    breakStartTime,
    breakHistory,
    breakAlertActive,
    waterGoal,
    waterConsumed,
    minutesUntilWater,
    waterAlertActive,
    hasActiveAlert,
    startBreak,
    endBreak,
    postponeBreak,
    setBreakInterval,
    drinkWater,
    setWaterGoal,
    toggleWellness,
    breakInterval,
  } = useWellness();

  const [expanded, setExpanded] = useState(false);

  if (!wellnessEnabled) {
    return (
      <div className="px-1 mb-2">
        <button
          onClick={toggleWellness}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs theme-text-muted hover:theme-text-secondary transition-colors"
        >
          <Coffee className="w-3.5 h-3.5" />
          <span>Activar bienestar</span>
        </button>
      </div>
    );
  }

  const waterPct = waterGoal > 0 ? Math.round((waterConsumed / waterGoal) * 100) : 0;
  const breakMinutes = isOnBreak && breakStartTime
    ? Math.round((Date.now() - breakStartTime) / 60000)
    : 0;

  return (
    <div
      className={`mx-1 mb-2 rounded-lg px-3 py-2.5 transition-all duration-300 ${
        hasActiveAlert
          ? "animate-pulse border border-cyan-400/50 bg-cyan-400/5"
          : "theme-bg-secondary"
      }`}
      style={!hasActiveAlert ? { border: '1px solid var(--theme-border)' } : undefined}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-1"
      >
        <span className="text-[11px] font-medium theme-text-muted uppercase tracking-wider flex items-center gap-1.5">
          <Coffee className="w-3 h-3" />
          Bienestar
        </span>
        {expanded ? (
          <ChevronUp className="w-3 h-3 theme-text-muted" />
        ) : (
          <ChevronDown className="w-3 h-3 theme-text-muted" />
        )}
      </button>

      {/* Compact view (always visible) */}
      <div className="space-y-1.5">
        {/* Break timer row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Timer className={`w-3.5 h-3.5 ${breakAlertActive ? "text-orange-400" : "theme-text-muted"}`} />
            <span className="text-xs theme-text-secondary">
              {isOnBreak ? (
                <span className="text-green-400">Descansando ({breakMinutes} min)</span>
              ) : minutesUntilBreak === 0 ? (
                <span className="text-orange-400 font-medium">Descansar!</span>
              ) : (
                `Descanso: ${minutesUntilBreak} min`
              )}
            </span>
          </div>
          {isOnBreak ? (
            <button onClick={endBreak} className="text-[10px] text-green-400 hover:text-green-300">
              Volver
            </button>
          ) : minutesUntilBreak === 0 ? (
            <button onClick={startBreak} className="text-[10px] text-cyan-400 hover:text-cyan-300 font-medium">
              Ir
            </button>
          ) : null}
        </div>

        {/* Water tracker row */}
        <div className="flex items-center gap-1.5">
          <Droplets className={`w-3.5 h-3.5 flex-shrink-0 ${waterAlertActive ? "text-blue-400" : "theme-text-muted"}`} />
          <div className="flex-1 flex items-center gap-2">
            <Progress value={waterPct} className="h-1.5 flex-1" />
            <span className="text-[10px] theme-text-muted whitespace-nowrap">{waterConsumed}/{waterGoal}</span>
          </div>
          {waterConsumed < waterGoal && (
            <button onClick={drinkWater} className="text-[10px] text-blue-400 hover:text-blue-300">
              +1
            </button>
          )}
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="mt-3 pt-2 space-y-3" style={{ borderTop: '1px solid var(--theme-border)' }}>
          {/* Break controls */}
          <div>
            <p className="text-[10px] font-medium theme-text-muted uppercase tracking-wider mb-1.5">Descansos</p>
            <div className="flex gap-1.5">
              {isOnBreak ? (
                <Button size="sm" variant="outline" onClick={endBreak} className="h-7 text-xs flex-1">
                  <Play className="w-3 h-3 mr-1" />
                  Volver al trabajo
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={startBreak} className="h-7 text-xs flex-1">
                    <Pause className="w-3 h-3 mr-1" />
                    Descansar
                  </Button>
                  {minutesUntilBreak === 0 && (
                    <Button size="sm" variant="ghost" onClick={() => postponeBreak(15)} className="h-7 text-xs">
                      +15m
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* Break config */}
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1 text-[10px] theme-text-muted hover:theme-text-secondary mt-1.5">
                  <Settings2 className="w-3 h-3" />
                  Intervalo: {breakInterval} min
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2" side="right" align="start">
                <p className="text-xs font-medium mb-2 theme-text">Intervalo de descanso</p>
                <div className="grid grid-cols-3 gap-1">
                  {[30, 45, 60, 90, 120].map((m) => (
                    <Button
                      key={m}
                      size="sm"
                      variant={breakInterval === m ? "default" : "ghost"}
                      onClick={() => setBreakInterval(m)}
                      className="h-7 text-xs"
                    >
                      {m}m
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Break history */}
            {breakHistory.length > 0 && (
              <div className="mt-1.5">
                <p className="text-[10px] theme-text-muted mb-0.5">Hoy: {breakHistory.length} descansos</p>
                <div className="flex flex-wrap gap-1">
                  {breakHistory.slice(-4).map((b, i) => (
                    <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-400">
                      {b.start} ({b.duration}m)
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Water controls */}
          <div>
            <p className="text-[10px] font-medium theme-text-muted uppercase tracking-wider mb-1.5">Hidratacion</p>
            <div className="flex items-center gap-2 mb-1.5">
              <Button size="sm" variant="outline" onClick={drinkWater} className="h-7 text-xs flex-1" disabled={waterConsumed >= waterGoal}>
                <Droplets className="w-3 h-3 mr-1" />
                {waterConsumed >= waterGoal ? "Meta cumplida!" : `Tome agua (${waterConsumed + 1}/${waterGoal})`}
              </Button>
            </div>

            {/* Water goal config */}
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1 text-[10px] theme-text-muted hover:theme-text-secondary">
                  <Settings2 className="w-3 h-3" />
                  Meta: {waterGoal} vasos
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2" side="right" align="start">
                <p className="text-xs font-medium mb-2 theme-text">Meta diaria de agua</p>
                <div className="grid grid-cols-4 gap-1">
                  {[4, 6, 8, 10].map((g) => (
                    <Button
                      key={g}
                      size="sm"
                      variant={waterGoal === g ? "default" : "ghost"}
                      onClick={() => setWaterGoal(g)}
                      className="h-7 text-xs"
                    >
                      {g}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {minutesUntilWater > 0 && waterConsumed < waterGoal && (
              <p className="text-[10px] theme-text-muted mt-1">
                Proximo recordatorio: {minutesUntilWater} min
              </p>
            )}
          </div>

          {/* Toggle off */}
          <button
            onClick={toggleWellness}
            className="text-[10px] theme-text-muted hover:text-red-400 transition-colors"
          >
            Desactivar bienestar
          </button>
        </div>
      )}
    </div>
  );
}
